import {FormattedMessage, useIntl} from "react-intl";
import {Alert, Button, Checkbox, Col, DatePicker, Divider, Form, Input, Radio, Row, Select, Space, Switch, Upload} from "antd";
import React, {ReactNode, Ref, SyntheticEvent, useContext, useEffect, useRef, useState} from "react";
import {
    Patient,
    HealthInsuranceCompany, Condition
} from "../../api-client";
import {PatientFormData} from "../../utils/patientUtils";
import {getPatientApi, getMedicalStaffApi, getSelfRegisterApi} from "../../api/apiInstances";
import {useNavigate, useParams} from "react-router-dom";
import dayjs, {Dayjs} from "dayjs";
import {validateEgnWithBirthDate} from "../../utils/medicalUtils";
import {patientNameValidator} from "../../utils/nameValidator";
import '../../assets/css/Dentobook.css';
import "../../assets/css/GeneralForm.css";
import {
    CameraOutlined,
    CaretDownOutlined,
    EnvironmentOutlined,
    HeartOutlined,
    MedicineBoxOutlined,
    UnorderedListOutlined,
    UserOutlined
} from "@ant-design/icons";
// @ts-ignore
import Tags from '@yaireo/tagify/dist/react.tagify';
import {isMobile, isMobileOnly} from "react-device-detect";
import UserIcon from "../common/UserIcon";
import FormButtons from "../common/FormButtons";
import ReactQuill from "react-quill";
import {AlertContext} from "../../context/AlertContext";
import {MAX_AVATAR_SIZE_BYTES} from "../common/Constants";
import {RcFile, UploadProps} from "antd/es/upload";
import {CheckboxValueType} from "antd/es/checkbox/Group";
import {AxiosResponse} from "axios";
import {ekatteMessages, location} from "../../i18n/ekatte";
import {messages} from "../../i18n/health-area";
import {healthRegionMessages} from "../../i18n/health-regions";
import {LOCALES} from "../../i18n/locales";
import {UploadFile} from "antd/es/upload/interface";
import {allCountries} from "../../i18n/countries";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {ExitPromptContext} from "../../context/ExitPromptContext";
import {arraysEqual, checkFormDirty} from "../../i18n/utils";
import logger from "../../utils/logger";
import {getPhoneCodeFromCountry, getPhoneCodeFromLocale} from "../../utils/countryUtils";
import {ALERT_DURATIONS} from "../../constants/alerts";
import {useTrackEvent} from "../../hooks/useTrackEvent";

const {Option} = Select;

// Mode constants for patient form
const SELF_REGISTER_MODE = 1; // Self-registration by patient
const CREATE_MODE = 2;         // Create patient (by admin/doctor/receptionist)

export interface PatientFormProps {
    patient: PatientFormData;
    setPatient?: Function;
    setEditMode: Function;
    openModal: Function;
    mode: number;
    children?: Element | Element[];
}

interface TagifyItem {
    value: string;
}

interface Region {
    key: string;
    value: string;
}

interface FieldsTranslations {
    [key: string]: string;
}

/**
 * Safely extract a human-readable error message from a FastAPI response.
 * Handles: string detail, 422 validation array, and unknown shapes.
 */
function extractErrorMessage(error: any): string | null {
    const detail = error.response?.data?.detail;
    if (!detail) return error.response?.data?.additionalMessage || error.response?.data?.message || null;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        // FastAPI 422: [{loc: [...], msg: "...", type: "..."}]
        return detail.map((e: any) => {
            const field = Array.isArray(e.loc) ? e.loc.filter((s: any) => s !== 'body').join('.') : '';
            return field ? `${field}: ${e.msg}` : e.msg;
        }).join('; ');
    }
    if (typeof detail === 'object' && detail.msg) return detail.msg;
    return JSON.stringify(detail);
}

const PatientForm = ({patient, setPatient, setEditMode, openModal, mode = CREATE_MODE}: PatientFormProps) => {
    // Type guard for existing patients (with id)
    // Type guard: id must be a non-empty string to be an existing patient
    const isExistingPatient = (p: PatientFormData): p is Patient =>
        typeof p.id === 'string' && p.id.length > 0;

    const intl = useIntl();
    const track = useTrackEvent();

    const navigate = useNavigate();

    const {id} = useParams();

    const {setFormDirty} = useContext(ExitPromptContext);

    const {userOrganization} = React.useContext(DentobookApplicationContext);

    // preselectedDoctors and isInitialized removed - doctors are assigned per treatment, not per patient

    const [tagsDirty, setTagsDirty] = useState<boolean>(false);

    const tagsInitialState = patient.conditions ?? [];

    const {setVisible, setError, setSuccessMessage, setRedirectUrl} = useContext(AlertContext);
    const {locale} = useContext(DentobookApplicationContext);

    const [imagePreview, setImagePreview] = useState<string>('');

    const [imagePreviewJsx, setImagePreviewJsx] = useState<ReactNode>();

    const [autocompleteIllnessTags, setAutocompleteIllnessTags] = useState<string[]>([]);
    const [autocompleteMedicationTags, setAutocompleteMedicationTags] = useState<string[]>([]);
    const [autocompleteAllergiesTags, setAutocompleteAllergiesTags] = useState<string[]>([]);

    // doctors state removed - doctor assignment happens per treatment

    const [healthInsurance, setHealthInsurance] = useState<'0' | '1' | null>(patient.healthInsuranceCompanyId ? '1' : '0');
    const [companies, setCompanies] = useState<HealthInsuranceCompany[]>([]);

    const [illnessTags, setIllnessTags] = useState<Array<string>>([]);
    const [medicationTags, setMedicationTags] = useState<Array<string>>([]);
    const [allergyTags, setAllergyTags] = useState<Array<string>>([]);

    const [femaleFieldVisible, setFemaleFieldVisible] = useState<boolean>(false);

    const [formPatient, setFormPatient] = useState({...patient});
    const [, setCountry] = useState<string>('');

    const [avatar, setAvatar] = useState<RcFile | undefined>(undefined);
    const [avatarMouseIn, setAvatarMouseIn] = useState<boolean>(false);

    const today = new Date();

    const [birthDate, setBirthDate] = useState<Dayjs | null>(
        formPatient.birthDate ? dayjs(formPatient.birthDate) : null
    );
    const [pregnancyPlanDate, setPregnancyPlanDate] = useState<Dayjs | null>(
        formPatient.pregnancyDate ? dayjs(formPatient.pregnancyDate) : null
    );
    const [lastVisitDate, setLastVisitDate] = useState<Dayjs | null>(
        formPatient.lastVisit ? dayjs(formPatient.lastVisit) : null
    );

    const [pregnancyDateVisible, setPregnancyDateVisible] = useState<boolean>(formPatient.pregnancyPlan === 'PREGNANT');

    const [isValidUrl, setIsValidUrl] = useState<boolean>(true);
    const [done, setDone] = useState<boolean>(false);

    const [organizationId, setOrganizationId] = useState<string>('');

    const [filteredRegions, setFilteredRegions] = useState<Region[]>([]);

    // Determine country code based on context
    const countryPhoneCode = mode === SELF_REGISTER_MODE
        ? getPhoneCodeFromLocale(locale) // Self-registration: use locale
        : getPhoneCodeFromCountry(userOrganization?.country ?? undefined); // Logged-in: use organization country

    const currentCountry = mode === SELF_REGISTER_MODE
        ? allCountries.find(c => getPhoneCodeFromCountry(c.code) === countryPhoneCode)
        : allCountries.find(c => c.code === userOrganization?.country);

    const countryName = locale === 'BG' ? currentCountry?.name_bg : currentCountry?.name_en;

    const translations: FieldsTranslations = messages[intl.locale].towns;

    const patientApi = getPatientApi();
    const medicalApi = getMedicalStaffApi();
    const selfRegisterApi = getSelfRegisterApi();

    const [form] = Form.useForm();

    const illnessTagifyRef: Ref<any> = useRef();
    const medicationTagifyRef: Ref<any> = useRef();
    const allergyTagifyRef: Ref<any> = useRef();

    let illnessTagsArray: Array<string> = [];
    let medicationTagsArray: Array<string> = [];
    let allergyTagsArray: Array<string> = [];

    const richTextEditorToolbarOptions = [
        [{'header': [1, 2, 3, 4, 5, 6, false]}],
        ['bold', 'italic', 'underline', 'strike']
    ];

    let fileValidationError = '';

    const filterOption = (input: string, option: any) => {
        const searchText = input.toLowerCase();
        return option?.searchLabel.includes(searchText) || option?.value.includes(searchText);
    };
    const handleHealthAreaChange = (value: string) => {
        setFormPatient({...formPatient, healthArea: value, healthRegion: ''});
        updateFilteredRegions(value);
    };

    const handleHealthRegionChange = (value: string) => {
        setFormPatient({...formPatient, healthRegion: value});
    };

    const handleEkkatteChange = (value: string) => {
        setFormPatient({...formPatient, ekatte: value});
    };

    const handleTownChange = (value: string) => {
        const selectedTown = location.find(item => item.value === value);
        console.table(selectedTown?.searchLabel)
        if (selectedTown) {
            setFormPatient({...formPatient, town: selectedTown.searchLabel});
        }
    };

    const updateFilteredRegions = (selectedArea: string) => {
        const regions = Object.entries(healthRegionMessages[intl.locale].healthRegions)
            .filter(([key]) => key.startsWith(selectedArea))
            .map(([key, value]) => ({key, value: value as string}));

        setFilteredRegions(regions);
    };

    const [formInitialState, setFormInitialState] = useState<Object | undefined>(undefined);

    useEffect(() => {
        if (!formInitialState) {
            setFormInitialState(form.getFieldsValue(true));
        }
    }, [form]);

    useEffect(() => {
        if (formPatient.birthDate) {
            dayjs(formPatient.birthDate)
        } else {
            setBirthDate(null);
        }
    }, [formPatient.birthDate]);

    useEffect(() => {
        if (formPatient.healthArea) {
            updateFilteredRegions(formPatient.healthArea);
        }
    }, [intl.locale, formPatient.healthArea]);

    useEffect(() => {
        if (imagePreview) {
            setImagePreviewJsx(
                <button onMouseEnter={() => setAvatarMouseIn(true)}
                        onMouseLeave={() => setAvatarMouseIn(false)}
                        className="avatar-upload-photo-img"
                >
                    <img width={150}
                         src={imagePreview}
                         alt="avatar"/>
                </button>
            );
        } else if (!patient.avatar) {
            setImagePreviewJsx(
                <button
                    onMouseEnter={() => setAvatarMouseIn(true)}
                    onMouseLeave={() => setAvatarMouseIn(false)}
                    className="avatar-upload-photo-img plain-button">
                    <UserIcon size="large" className="mb-4"/>
                </button>
            );
        } else {
            const avatarSrc = patient.avatarUrl ?? undefined;
            setImagePreviewJsx(
                <button onMouseEnter={() => setAvatarMouseIn(true)}
                        onMouseLeave={() => setAvatarMouseIn(false)}
                        className="avatar-upload-photo-img">
                    <img width={150}
                         src={avatarSrc}
                         alt="avatar"/>
                </button>
            )
        }
    }, [imagePreview, patient.avatar, patient.avatarUrl]);

    useEffect(() => {
        setFemaleFieldVisible(formPatient.gender === 'female');

        if (formPatient.conditions) {
            const illnessTagsArr = formPatient.conditions.filter(item => item.type === 'ILLNESS')
                .map(item => item.title || '');

            setIllnessTags(illnessTagsArr);

            const medicationTagsArr = formPatient.conditions.filter(item => item.type === 'MEDICATION')
                .map(item => item.title || '');

            setMedicationTags(medicationTagsArr);

            const allergyTagsArr = formPatient.conditions.filter(item => item.type === 'ALLERGY')
                .map(item => item.title || '');

            setAllergyTags(allergyTagsArr);
        }

        if (mode === SELF_REGISTER_MODE) {
            selfRegisterApi.validatePatientCreateUrlId(id ?? '')
                .then(res => {
                    const organizationIdRes = res.data.organizationId!;
                    setOrganizationId(organizationIdRes);

                    selfRegisterApi.fetchHealthInsuranceCompaniesAnonymously(countryPhoneCode, organizationIdRes)
                        .then(res => {
                            setCompanies(res.data);
                            selfRegisterApi.fetchPatientConditionsAnonymously(organizationIdRes)
                                .then(res => setTagElements(res.data))
                                .catch(err => logger.log(err));
                        })
                })
                .catch(err => {
                    logger.log(err);
                    setIsValidUrl(false);
                });
        } else {
            // Load all data independently in parallel - no unnecessary coupling
            medicalApi.fetchAllInsuranceCompanies(countryPhoneCode)
                .then(res => setCompanies(res.data.map(c => ({
                    id: c.id,
                    name: c.name,
                    countryCode: '' // HealthInsuranceCompanyResponse doesn't have countryCode
                }))))
                .catch(err => logger.log(err));

            patientApi.getAllConditions()
                .then(res => setTagElements(res.data))
                .catch(err => logger.log(err));
        }
    }, []);

    // Doctor preselection removed - doctors are assigned per treatment, not per patient

    const updateAvatar = (response: AxiosResponse, successMessage: string) => {
        if (avatar) {
            patientApi.uploadPatientAvatar(response.data.id!, avatar)
                .then(() => {
                    if (mode !== 1) {
                        setRedirectUrl(`/patients/${response.data.id}`);
                        setVisible(true);
                        setError('');
                        setSuccessMessage(successMessage);
                        setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
                        navigate(`/patients/${response.data.id}`);
                    } else {
                        setDone(true);
                        showSuccessAlert();
                    }
                })
                .catch(err => {
                    avatarFailAction(err);

                    if (mode !== 1) {
                        navigate(`/patients/${response.data.id}`);
                    }
                });
        } else if (mode !== 1) {
            setRedirectUrl(`/patients/${response.data.id}`);
            setVisible(true);
            setError('');
            setSuccessMessage(successMessage);
            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
            navigate(`/patients/${response.data.id}`);
        } else {
            setDone(true);
        }
    };

    const setTagElements = (data: Array<Condition>) => {
        // Backend condition data structure:
        // - title: Bulgarian text (default)
        // - titleEnglish: English text
        // Use English titles for EN locale, Bulgarian for all others (BG, TR, etc.)
        const useEnglishTitles = locale === LOCALES.ENGLISH;

        const illnessTagsArr = data.filter(item => item.type === 'ILLNESS')
            .map(item => (useEnglishTitles ? item.titleEnglish : item.title) || '');

        setAutocompleteIllnessTags(illnessTagsArr);

        const medicationTagsArr = data.filter(item => item.type === 'MEDICATION')
            .map(item => (useEnglishTitles ? item.titleEnglish : item.title) || '');

        setAutocompleteMedicationTags(medicationTagsArr);

        const allergyTagsArr = data.filter(item => item.type === 'ALLERGY')
            .map(item => (useEnglishTitles ? item.titleEnglish : item.title) || '');

        setAutocompleteAllergiesTags(allergyTagsArr);
    }

    const avatarFailAction = (error: ErrorEvent) => {
        logger.log(error);

        setDone(true);

        setVisible(true);
        setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);

        setError(intl.formatMessage({id: 'avatar_upload_error'}));
        setTimeout(() => {
            setError('');
        }, ALERT_DURATIONS.STANDARD);
    }

    const showSuccessAlert = () => {
        setError('');
        setSuccessMessage('Създаден е нов пациент');
        setVisible(true);
        setTimeout(() => {
            setVisible(false);
            setSuccessMessage('');
        }, ALERT_DURATIONS.STANDARD);
    }

    const handleSubmit = async (e: SyntheticEvent) => {
        e.preventDefault();

        try {
            form.submit();

            await form.validateFields();

            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");

            let tags = [];

            if (birthDate && userOrganization?.country === 'BG') {
                const isValidEgn = validateEgnWithBirthDate(formPatient.egn as string, birthDate);

                if (!isValidEgn) {
                    setError(intl.formatMessage({id: 'identifierAlert'}));
                    setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
                    return;
                }
            }

            if (illnessTagifyRef.current?.value) {
                tags.push(...illnessTagifyRef.current.value.map((item: TagifyItem) => {
                    return {title: item.value, type: 'ILLNESS'};
                }));
            } else {
                tags.push(...illnessTagsArray.map(item => {
                    return {title: item, type: 'ILLNESS'};
                }));
            }

            if (medicationTagifyRef.current?.value) {
                tags.push(...medicationTagifyRef.current.value.map((item: TagifyItem) => {
                    return {title: item.value, type: 'MEDICATION'};
                }));
            } else {
                tags.push(...medicationTagsArray.map(item => {
                    return {title: item, type: 'MEDICATION'};
                }));
            }

            if (allergyTagifyRef.current?.value) {
                tags.push(...allergyTagifyRef.current.value.map((item: TagifyItem) => {
                    return {title: item.value, type: 'ALLERGY'};
                }));
            } else {
                tags.push(...allergyTagsArray.map(item => {
                    return {title: item, type: 'ALLERGY'};
                }));
            }

            formPatient.birthDate = birthDate ? birthDate.toISOString() : undefined;
            formPatient.pregnancyDate = pregnancyPlanDate ? pregnancyPlanDate.toISOString() : undefined;
            formPatient.lastVisit = lastVisitDate ? lastVisitDate.toISOString() : undefined;
            formPatient.conditions = tags;
            const successMessageCreate = intl.formatMessage({ id: 'new_patient' });
            const successMessageUpdate = intl.formatMessage({ id: 'patient_updated' });


            if (mode === SELF_REGISTER_MODE && id) {
                formPatient.organizationId = organizationId;
                selfRegisterApi.createPatientAnonymously(id, formPatient)
                    .then(response => updateAvatar(response, successMessageCreate))
                    .catch(error => {
                        const errorMessage = extractErrorMessage(error);

                        if (errorMessage) {
                            const msg = errorMessage.toLowerCase();

                            if (msg.indexOf('email') > -1) {
                                setError(intl.formatMessage({id: 'duplicate_email'}));
                            } else if (msg.indexOf('expired') > -1 || msg.indexOf('link') > -1) {
                                setError(intl.formatMessage({id: 'link_expired'}));
                            } else {
                                setError(errorMessage);
                            }
                            setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
                        }

                        logger.log('error', error)
                    });
            } else if (!patient.id) {
                patientApi.createPatient(formPatient)
                    .then(response => {
                        track('patient_created');
                        return updateAvatar(response, successMessageCreate);
                    })
                    .then(() => {
                        if (setPatient) {
                            setPatient({...formPatient});
                        }
                    })
                    .catch(error => {
                        const errorMessage = extractErrorMessage(error);

                        if (errorMessage) {
                            const msg = errorMessage.toLowerCase();
                            if (msg.indexOf('email') > -1) {
                                setError(intl.formatMessage({id: 'duplicate_email'}));
                            } else {
                                setError(errorMessage);
                            }
                            setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
                        }

                        logger.log('error', error)
                    });
            } else {
                patientApi.updatePatient(patient.id, formPatient)
                    .then(response => updateAvatar(response, successMessageUpdate))
                    .then(() => {
                        if (setPatient) {
                            setPatient({...formPatient});
                        }
                        setEditMode(false);
                    })
                    .catch(error => {
                        const errorMessage = extractErrorMessage(error);

                        if (errorMessage) {
                            const msg = errorMessage.toLowerCase();
                            if (msg.indexOf('email') > -1) {
                                setError(intl.formatMessage({id: 'duplicate_email'}));
                            } else {
                                setError(errorMessage);
                            }
                            setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
                        }
                        logger.log('error', error);
                    });
            }
        } catch (err: any) {
            logger.log(err);

            const blankFieldExists = Object.values(err.values).filter(item => !item).length > 0;

            if (blankFieldExists) {
                setError(intl.formatMessage({id: 'all_fields_required'}));
                setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
            }
        }
    }

    const props: UploadProps = {
        beforeUpload: (file: UploadFile) => {
            const fileName = file.name.toLowerCase();
            const validFormat = fileName.endsWith('png') || fileName.endsWith('jpg') || fileName.endsWith('jpeg');
            const validSize = file.size !== undefined && file.size <= MAX_AVATAR_SIZE_BYTES;

            if (!validFormat) {
                fileValidationError = intl.formatMessage({id: 'invalid_image_format'});
            } else if (!validSize) {
                fileValidationError = intl.formatMessage({id: 'invalid_image_size'});
            } else {
                fileValidationError = '';
            }
        },
        name: 'file',
        customRequest: () => {
        },
        showUploadList: false,
        onChange(info) {
            if (!fileValidationError) {
                setAvatar(info.file.originFileObj);
                setImagePreview(URL.createObjectURL(info.file.originFileObj as Blob));
            } else {
                setError(fileValidationError);
                setTimeout(() => {
                    setVisible(false)
                    setError('');
                }, ALERT_DURATIONS.STANDARD);
            }
        },
    };

    const handleTagifyChange = (tagType: string, tagsArr: Array<object>) => {
        const initialState = tagsInitialState
            .filter(item => item.type === tagType)
            .map(item => item.title);
        const currentState = tagsArr.map((item: any) => item.value);
        const dirty = !arraysEqual(initialState, currentState);

        setTagsDirty(dirty);
        if (dirty) {
            setFormDirty(dirty);
        }
    }

    if (mode === SELF_REGISTER_MODE) {
        if (!isValidUrl) {
            return (
                <h1 className="text-center"><FormattedMessage id="invalid_url"/></h1>
            );
        }
        if (done) {
            return (
                <h1 className="text-center mt-10"><FormattedMessage id="successfully_registered"/></h1>
            );
        }
    }

    return (
        <Form form={form} scrollToFirstError={true} name="horizontal_login" layout="vertical"
              onFieldsChange={(changedFields, allFields) => {
                  const condition = tagsDirty || checkFormDirty(allFields, formInitialState);
                  setFormDirty(condition);
              }}>
            <h4><span className="text-blue mr-2"><UserOutlined/></span> {<FormattedMessage
                id="personal_information"/>}
            </h4>
            <Upload {...props}>
                <div className={avatarMouseIn ? "avatar-upload-photo-mask" : ""}>
                    <CameraOutlined className={avatarMouseIn ? "avatar-upload-icon" : "d-none"}/>
                    {imagePreviewJsx}
                </div>
            </Upload>
            <Row gutter={24} className="mt-4">
                <Col flex={1.5}>
                    <Form.Item
                        label={<FormattedMessage id="first_name"/>}
                        className={"dentobook-form-item dentobook-simple-input"}
                        name="FirstName"
                        initialValue={formPatient.firstName}
                        validateTrigger={['onBlur', 'onChange']}
                        rules={[{
                            validator: patientNameValidator(intl.formatMessage({id: 'letters_only_field'}))
                        }, {
                            max: 100,
                            message: <FormattedMessage id="field_too_long_message"/>
                        },
                            {required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <Input
                            value={formPatient.firstName}
                            onChange={e => formPatient.firstName = e.target.value}/>
                    </Form.Item>
                </Col>
                <Col flex={1.5}>
                    <Form.Item
                        label={<FormattedMessage id="surname"/>}
                        name="Surname"
                        className={"dentobook-form-item dentobook-simple-input"}
                        validateTrigger={['onBlur', 'onChange']}
                        rules={[{
                            validator: patientNameValidator(intl.formatMessage({id: 'letters_only_field'}))
                        }, {
                            max: 100,
                            message: <FormattedMessage id="field_too_long_message"/>
                        }]}
                        initialValue={formPatient.secondName}
                    >
                        <Input
                            value={formPatient.secondName ?? undefined}
                            onChange={e => formPatient.secondName = e.target.value}/>
                    </Form.Item>
                </Col>
                <Col flex={2}>
                    <Form.Item
                        label={<FormattedMessage id="last_name"/>}
                        name="LastName"
                        initialValue={formPatient.lastName}
                        className={"dentobook-form-item dentobook-simple-input"}
                        validateTrigger={['onBlur', 'onChange']}
                        rules={[{
                            validator: patientNameValidator(intl.formatMessage({id: 'letters_only_field'}))
                        }, {
                            max: 100,
                            message: <FormattedMessage id="field_too_long_message"/>
                        },
                            {required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <Input
                            value={formPatient.lastName}
                            onChange={e => formPatient.lastName = e.target.value}/>
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={24}>
                <Col flex={1.47}>
                    <Form.Item name="gender"
                               className={"dentobook-form-item single-select"}
                               label={<FormattedMessage id="gender"/>}
                               initialValue={formPatient.gender}>
                        <Select
                            size={"large"}
                            className="dentobook-input"
                            value={formPatient.gender}
                            suffixIcon={<CaretDownOutlined/>}
                            onChange={e => {
                                formPatient.gender = e;
                                setFemaleFieldVisible(e === 'female');
                            }}
                            allowClear
                        >
                            <Option value="male"><FormattedMessage id="gender_male"/></Option>
                            <Option value="female"><FormattedMessage id="gender_female"/></Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col flex={1.63}>
                    <Form.Item
                        className={"dentobook-form-item"}
                        label={<FormattedMessage id="birth_date"/>}
                        name="birthDate"
                        initialValue={birthDate}>
                        <DatePicker
                            disabledDate={date => date.isAfter(today)}
                            size={"large"}
                            format={"DD.MM.YYYY"}
                            value={birthDate}
                            onChange={e => setBirthDate(e)}
                            placeholder=""/>
                    </Form.Item>
                </Col>
                <Col flex={1.9}>
                    <Form.Item
                        name="egn"
                        className={"dentobook-form-item dentobook-simple-input"}
                        rules={[{
                            pattern: /^\d*$/,
                            message: <FormattedMessage id="digits_only_field"/>
                        },
                            {required: true, message: <FormattedMessage id="required_field"/>}]}
                        initialValue={formPatient.egn}
                        label={<FormattedMessage id="egn"/>}>
                        <Input
                            value={formPatient.egn ?? undefined}
                            onChange={e => formPatient.egn = e.target.value}/>
                    </Form.Item>
                </Col>
            </Row>
            {mode === CREATE_MODE ?
                <Row gutter={[24, 24]}>
                    <Col>
                        <Form.Item
                            label={<FormattedMessage id="profession"/>}
                            name="profession"
                            className={"dentobook-form-item dentobook-simple-input"}
                            initialValue={formPatient.profession}
                        >
                            <Input
                                style={!isMobileOnly ? {width: 407} : {width: '88vw'}}
                                value={formPatient.profession ?? undefined}
                                onChange={e => formPatient.profession = e.target.value}/>
                        </Form.Item>
                    </Col>
                </Row> : null}
            {/* Registration section removed: all fields (registeredOn, lastAppointment,
               nextAppointment, treatingDentists) are now shown in the read-only PatientDetailsView
               using live data derived from CalendarEvent and TreatmentTooth. */}
            <Divider/>
            <h4><span className="text-blue mr-1"><EnvironmentOutlined/></span> <FormattedMessage id="contact"/></h4>
            <Row gutter={24}>
                <Col flex={1.25}>
                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="email"/>}
                        name="email"
                        rules={[{
                            type: "email",
                            message: <FormattedMessage id="invalid_email"/>
                        }]}
                        initialValue={formPatient.email}
                    >
                        <Input
                            value={formPatient.email ?? undefined}
                            onChange={e => formPatient.email = e.target.value || null}/>
                    </Form.Item>
                </Col>
                <Col flex={1.25}>
                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="phone_label_two"/>}
                        name="phone"
                        rules={[{
                            pattern: /^\+[1-9]\d{1,14}$/,
                            message: <FormattedMessage id="invalid_phone_e164"/>
                        }]}
                        initialValue={formPatient.phone}
                    >
                        <Input type="tel"
                               value={formPatient.phone ?? undefined}
                               onChange={e => formPatient.phone = e.target.value}/>
                    </Form.Item>
                </Col>
                <Col flex={1.25}>
                    <Form.Item
                        className="dentobook-form-item input-full-width single-select"
                        label={<FormattedMessage id="town"/>}
                        name="town"
                        rules={[{
                            message: <FormattedMessage id="letters_only_field"/>
                        }]}
                        initialValue={formPatient.town}
                    >
                       <Select
                                className="dentobook-input"
                                showSearch
                                suffixIcon={<CaretDownOutlined/>}
                                placeholder="Търсене и избор"
                                optionFilterProp="label"
                                filterOption={filterOption}
                                onChange={handleTownChange}
                                options={location}
                            />
                    </Form.Item>
                </Col>
                <Col flex={1.25}>
                    <Form.Item
                        className="dentobook-form-item input-full-width single-select"
                        label={<FormattedMessage id="country" />}
                        name="country"
                        initialValue={formPatient.country}
                        rules={[{
                            message: <FormattedMessage id="letters_only_field"/>
                        }]}
                    >
                        <Select
                            suffixIcon={<CaretDownOutlined />}
                            className="dentobook-input"
                            showSearch
                            placeholder="Търсене и избор"
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            value={formPatient.country}
                            onChange={(value) => {
                                formPatient.country = value;
                                setCountry(value);
                            }}
                            options={allCountries.map(countryItem => ({
                                value: countryItem.code,
                                label: locale === 'BG' ? countryItem.name_bg : countryItem.name_en,
                            }))}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24} className="mt-0">
                <Col flex={1.6}>
                    <Form.Item
                        className="dentobook-form-item input-full-width single-select"
                        label={<FormattedMessage id="healthArea"/>}
                        name="healthArea"
                        initialValue={formPatient.healthArea}
                        rules={[{
                            message: <FormattedMessage id="letters_only_field"/>
                        }]}
                    >
                        <Select
                            suffixIcon={<CaretDownOutlined/>}
                            className="dentobook-input"
                            showSearch
                            placeholder="Търсене и избор"
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            value={formPatient.healthArea}
                            onChange={handleHealthAreaChange}
                            options={
                                Object.entries(translations)
                                    .sort((a, b) => a[1].localeCompare(b[1]))
                                    .map(([key, value]) => ({
                                        value: key,
                                        label: `${intl.formatMessage({ id: "rhif" })} ${value}`
                                    }))
                            }
                        />
                    </Form.Item>
                </Col>
                <Col flex={1.6}>
                    <Form.Item
                        className={"dentobook-form-item input-full-width single-select"}
                        label={<FormattedMessage id="healthRegion"/>}
                        name="healthRegion"
                        initialValue={formPatient.healthRegion}
                        rules={[{
                            message: <FormattedMessage id="letters_only_field"/>
                        }]}
                    >
                        <Select
                            showSearch
                            optionFilterProp="children"
                            suffixIcon={<CaretDownOutlined/>}
                            className="dentobook-input"
                            value={formPatient.healthRegion}
                            onChange={handleHealthRegionChange}
                            disabled={!formPatient.healthArea}
                        >
                            {filteredRegions.map(({key, value}) => (
                                <Select.Option key={key} value={key}>{value}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col flex={1.8}>
                    <Form.Item
                        className={"dentobook-form-item input-full-width single-select"}
                        label={<FormattedMessage id="ekkatte"/>}
                        name="ekkatte"
                        initialValue={formPatient.ekatte}
                        rules={[{
                            message: <FormattedMessage id="letters_only_field"/>
                        }]}
                    >
                        <Select
                            className="dentobook-input"
                            showSearch
                            suffixIcon={<CaretDownOutlined/>}
                            placeholder="Търсене и избор"
                            optionFilterProp="label"
                            filterOption={filterOption}
                            onChange={handleEkkatteChange}
                            options={ekatteMessages}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Form.Item
                    className={"dentobook-form-item dentobook-simple-input input-fill-width"}
                    label={<FormattedMessage id="addressOne"/>}
                    name="addressOne"
                    initialValue={formPatient.addressOne}
                >
                    <Input value={formPatient.addressOne ?? undefined}
                           onChange={e => formPatient.addressOne = e.target.value}/>
                </Form.Item>
            </Row>
            <Row gutter={24} style={{marginTop: 8}}>
                <Col>
                    <Form.Item
                        className="dentobook-form-item"
                        label={<FormattedMessage id="preferred_notification_channel"/>}
                        name="preferredNotificationChannel"
                        initialValue={(formPatient as any).preferredNotificationChannel ?? 'email'}
                    >
                        <Select
                            style={{width: 160}}
                            onChange={val => {
                                (formPatient as any).preferredNotificationChannel = val;
                                if (val === 'sms' || val === 'whatsapp') {
                                    (formPatient as any).phoneNotificationsEnabled = true;
                                    form.setFieldsValue({phoneNotificationsEnabled: true});
                                } else if (val === 'email') {
                                    (formPatient as any).emailNotificationsEnabled = true;
                                    form.setFieldsValue({emailNotificationsEnabled: true});
                                }
                            }}
                            options={[
                                {value: 'email', label: intl.formatMessage({id: 'channel_email'})},
                                {value: 'sms', label: intl.formatMessage({id: 'channel_sms'})},
                                {value: 'whatsapp', label: intl.formatMessage({id: 'channel_whatsapp'})},
                            ]}
                        />
                    </Form.Item>
                </Col>
                <Col>
                    <Form.Item
                        className="dentobook-form-item"
                        label={<FormattedMessage id="patient_email_allowed"/>}
                        name="emailNotificationsEnabled"
                        valuePropName="checked"
                        initialValue={(formPatient as any).emailNotificationsEnabled ?? true}
                    >
                        <Switch
                            onChange={checked => (formPatient as any).emailNotificationsEnabled = checked}
                        />
                    </Form.Item>
                </Col>
                <Col>
                    <Form.Item
                        className="dentobook-form-item"
                        label={<FormattedMessage id="patient_phone_allowed"/>}
                        name="phoneNotificationsEnabled"
                        valuePropName="checked"
                        initialValue={(formPatient as any).phoneNotificationsEnabled ?? true}
                    >
                        <Switch
                            onChange={checked => (formPatient as any).phoneNotificationsEnabled = checked}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <div style={{color: '#888', fontSize: 12, marginTop: -8, marginBottom: 8}}>
                <FormattedMessage id="patient_channel_helper"/>
            </div>

            <Divider/>
            <h4><span className="text-blue mr-2"><MedicineBoxOutlined/></span> <FormattedMessage
                id="general_medical_history"/></h4>
            <div>
                <span className="font-16"><FormattedMessage id="general_illness_suffering"/></span>
                <Row className="input-ninety-eight-percent borderless">
                    <Col flex={5}>
                        {mode === CREATE_MODE ?
                            <div className="borderless mt-2">
                                <span className="font-weight-400"><FormattedMessage id="illnesses"/></span>
                                <Tags
                                    value={illnessTags} tagifyRef={illnessTagifyRef}
                                    whitelist={autocompleteIllnessTags}
                                    settings={{
                                        blacklist: [],
                                        maxTags: 10,
                                        dropdown: {
                                            enabled: 0
                                        },
                                        callbacks: {}
                                    }}
                                    onChange={(e: any) => handleTagifyChange('ILLNESS', e.detail.tagify.getCleanValue())}
                                />
                            </div>
                            : <div className="mt-2">
                                <Form.Item name="illnesses-group">
                                    <Checkbox.Group value={illnessTags}
                                                    onChange={(e: CheckboxValueType[]) =>
                                                        illnessTagsArray = e.map(item => item.toString())}>
                                        <Space direction="vertical">
                                            {autocompleteIllnessTags.map(tagItem => (
                                                <Checkbox key={tagItem} value={tagItem}>{tagItem}</Checkbox>
                                            ))}
                                        </Space>
                                    </Checkbox.Group>
                                </Form.Item>
                            </div>}
                    </Col>
                </Row>
            </div>
            <div className="mt-24">
                <span className="font-16"><FormattedMessage id="medications_taking"/></span>
                <Row className="input-ninety-eight-percent borderless">
                    <Col flex={5}>
                        {mode === CREATE_MODE ? <div className="borderless mt-2">
                            <span className="font-weight-400"><FormattedMessage id="medications"/></span>
                            <Tags
                                className="mt-1"
                                value={medicationTags} tagifyRef={medicationTagifyRef}
                                whitelist={autocompleteMedicationTags}
                                settings={{
                                    blacklist: [],
                                    maxTags: 10,
                                    dropdown: {
                                        enabled: 0
                                    },
                                    callbacks: {}
                                }}
                                onChange={(e: any) => handleTagifyChange("MEDICATION", e.detail.tagify.getCleanValue())}/>
                        </div> : <div className="mt-2">
                            <Form.Item name="medications-group">
                                <Checkbox.Group value={medicationTags}
                                                onChange={(e: CheckboxValueType[]) =>
                                                    medicationTagsArray = e.map(item => item.toString())}>
                                    <Space direction="vertical">
                                        {autocompleteMedicationTags.map(tagItem => (
                                            <Checkbox key={tagItem} value={tagItem}>{tagItem}</Checkbox>
                                        ))}
                                    </Space>
                                </Checkbox.Group>
                            </Form.Item>
                        </div>}
                    </Col>
                </Row>
            </div>
            <div className="mt-16">
                <span className="font-16"><FormattedMessage id="allergy_having"/></span>
                <Row className="input-ninety-eight-percent borderless">
                    <Col flex={5}>
                        {mode === CREATE_MODE ? <div className="borderless mt-2">
                            <span className="font-weight-400"><FormattedMessage id="allergies"/></span>
                            <Tags
                                className="mt-1"
                                value={allergyTags} tagifyRef={allergyTagifyRef}
                                whitelist={autocompleteAllergiesTags}
                                settings={{
                                    blacklist: [],
                                    maxTags: 10,
                                    dropdown: {
                                        enabled: 0
                                    },
                                    callbacks: {}
                                }}
                                onChange={(e: any) => handleTagifyChange('ALLERGY', e.detail.tagify.getCleanValue())}/>
                        </div> : <div className="mt-2">
                            <Form.Item name="allergies-group">
                                <Checkbox.Group value={allergyTags}
                                                onChange={(e: CheckboxValueType[]) =>
                                                    allergyTagsArray = e.map(item => item.toString())}>
                                    <Space direction="vertical">
                                        {autocompleteAllergiesTags.map(tagItem => (
                                            <Checkbox key={tagItem} value={tagItem.toString()}>{tagItem}</Checkbox>
                                        ))}
                                    </Space>
                                </Checkbox.Group>
                            </Form.Item>
                        </div>}
                    </Col>
                </Row>
            </div>
            {femaleFieldVisible ?
                <>
                    <div className="mt-4">
                        <span className="font-16"><FormattedMessage id="pregnancy_planning"/></span>
                        <Form.Item className="mt-2">
                            <Radio.Group
                                name="pregnancyPlan"
                                defaultValue={formPatient.pregnancyPlan}
                                onChange={e => {
                                    formPatient.pregnancyPlan = e.target.value;
                                    form.setFieldValue('pregnancyPlan', e.target.value);

                                    const isPregnant = e.target.value === 'PREGNANT';
                                    if (!isPregnant) {
                                        setPregnancyPlanDate(null);
                                    }
                                    setPregnancyDateVisible(isPregnant);
                                }}>
                                <Space direction="vertical">
                                    <Radio value="NO"><FormattedMessage id="no_ans"/></Radio>
                                    <Radio value="YES"><FormattedMessage id="yes_ans"/></Radio>
                                    <Radio value="PREGNANT"><FormattedMessage id="pregnant_ans"/></Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        {pregnancyDateVisible ?
                            <Form.Item
                                className={"dentobook-form-item input-full-width"}
                                name="pregnancyDate"
                                initialValue={pregnancyPlanDate}>
                                <DatePicker
                                    size={"large"}
                                    format="DD.MM.YYYY"
                                    value={pregnancyPlanDate}
                                    onChange={e => setPregnancyPlanDate(e)}
                                />
                            </Form.Item> : null}
                    </div>
                    <div className="mt-4">
                        <span className="font-16"><FormattedMessage id="nursing_question"/></span>
                        <Form.Item className="mt-2">
                            <Radio.Group
                                name="nursing"
                                defaultValue={String(formPatient.nursing)}
                                onChange={e => formPatient.nursing = e.target.value}
                            >
                                <Space direction="vertical">
                                    <Radio value="false"><FormattedMessage id="no_ans"/></Radio>
                                    <Radio value="true"><FormattedMessage id="yes_ans"/></Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                    </div>
                </>
                : null}
            <Divider/>
            <h4><span className="text-blue mr-2"><HeartOutlined/></span> <FormattedMessage
                id="anamensis_title"/></h4>
            <div className="mt-4">
                <span className="font-16"><FormattedMessage id="anesthetic_question"/></span>
                <Form.Item className="mt-12">
                    <Radio.Group
                        name="anesthetic"
                        defaultValue={String(formPatient.anesthesia)}
                        onChange={e => formPatient.anesthesia = e.target.value}>
                        <Space direction="vertical">
                            <Radio value="false"><FormattedMessage id="no_ans"/></Radio>
                            <Radio value="true"><FormattedMessage id="yes_ans"/></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
            </div>
            <div className="mt-4">
                <span className="font-16"><FormattedMessage id="higiene_question"/></span>
                <Form.Item className="mt-12">
                    <Radio.Group
                        name="higiene"
                        defaultValue={String(formPatient.regularHygiene)}
                        onChange={e => formPatient.regularHygiene = e.target.value}>
                        <Space direction="vertical">
                            <Radio value="false"><FormattedMessage id="no_ans"/></Radio>
                            <Radio value="true"><FormattedMessage id="yes_ans"/></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
            </div>

            <Divider/>
            <h4><span className="text-blue mr-2"><UnorderedListOutlined/></span> <FormattedMessage
                id="social_anamnezis"/></h4>

            <div className="mt-4">
                <span className="font-16"><FormattedMessage id="insurance_question"/></span>
                <Form.Item className="mt-12"
                           initialValue={null}>
                    <Radio.Group
                        name="healthInsurance"
                        value={healthInsurance}
                        onChange={e => setHealthInsurance(e.target.value)}>
                        <Space direction="vertical">
                            <Radio value="0"><FormattedMessage id="no_ans"/></Radio>
                            <Radio value="1"><FormattedMessage id="yes_ans"/></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
                {healthInsurance === '1' ?
                    <>
                        <Alert
                            message={intl.formatMessage(
                                { id: 'health_insurance_filter_info' },
                                { country: countryName || 'Bulgaria' }
                            )}
                            type="info"
                            showIcon
                            className="mb-3"
                        />
                        <Form.Item name="insurer"
                                   className={"dentobook-form-item input-full-width"}
                                   label={<FormattedMessage id="insurer"/>}
                                   initialValue={formPatient.healthInsuranceCompanyId}>
                            <Select
                                size={"large"}
                                className="dentobook-input"
                                value={formPatient.healthInsuranceCompanyId}
                                suffixIcon={<CaretDownOutlined/>}
                                onChange={e => formPatient.healthInsuranceCompanyId = e}
                                allowClear
                            >
                                {companies.map(company => (
                                    <Option key={company.id} value={company.id}>{company.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </> : null}
            </div>
            <div className="mt-4">
                <span className="font-16"><FormattedMessage id="dentist_visit_question"/></span>
                <Form.Item className="mt-12">
                    <Radio.Group
                        name="higiene"
                        defaultValue={formPatient.visitPeriod}
                        onChange={e => formPatient.visitPeriod = e.target.value}>
                        <Space direction="vertical">
                            <Radio value="TWICE_A_YEAR"><FormattedMessage id="dentist_visit_ans1"/></Radio>
                            <Radio value="ONCE_A_YEAR"><FormattedMessage id="dentist_visit_ans2"/></Radio>
                            <Radio value="RARELY"><FormattedMessage id="dentist_visit_ans3"/></Radio>
                            <Radio value="ONLY_WHEN_NECESSARY"><FormattedMessage id="dentist_visit_ans4"/></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
            </div>
            <div className="mt-4">
                <span className="font-16"><FormattedMessage id="dentist_last_visit_question"/></span>
                <br/>
                <Space className="mt-24" direction={isMobileOnly ? 'vertical' : 'horizontal'}>
                    <Form.Item
                        className={"dentobook-form-item input-full-width"}
                        initialValue={lastVisitDate}
                        label={<FormattedMessage id="date"/>}
                    >
                        <DatePicker
                            placeholder=""
                            disabledDate={date => date.isAfter(new Date())}
                            size={"large"}
                            format="DD.MM.YYYY"
                            value={lastVisitDate}
                            onChange={e => setLastVisitDate(e)}
                        />
                    </Form.Item>
                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        style={isMobileOnly ? {} : {width: '60vw'}}
                        label={<FormattedMessage id="reason"/>}
                        name="lastVisitReason"
                        initialValue={formPatient.lastVisitReason}
                    >
                        <Input value={formPatient.lastVisitReason ?? undefined}
                               onChange={e => formPatient.lastVisitReason = e.target.value}/>
                    </Form.Item>
                </Space>
            </div>

            <div>
                <span className="font-16"><FormattedMessage id="fear_question"/></span>
                <Form.Item className="mt-12">
                    <Radio.Group
                        name="fear"
                        defaultValue={formPatient.fear}
                        onChange={e => formPatient.fear = e.target.value}>
                        <Space direction="vertical">
                            <Radio value="NO"><FormattedMessage id="no_ans"/></Radio>
                            <Radio value="YES"><FormattedMessage id="yes_ans"/></Radio>
                            <Radio value="SOMETIMES"><FormattedMessage id="fear_ans"/></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
            </div>
            <div style={mode === SELF_REGISTER_MODE ? {marginBottom: 150} : {}}>
                <span className="font-16"><FormattedMessage id="smoker_question"/></span>
                <Form.Item className="mt-12">
                    <Radio.Group
                        name="isSmoker"
                        defaultValue={formPatient.isSmoker == null ? null : (+(formPatient.isSmoker ?? false)).toString()}
                        onChange={e => formPatient.isSmoker = !!+e.target.value}>
                        <Space direction="vertical">
                            <Radio value="0"><FormattedMessage id="no_ans"/></Radio>
                            <Radio value="1"><FormattedMessage id="yes_ans"/></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
            </div>

            {mode === CREATE_MODE ?
                <Form.Item
                    label={<FormattedMessage id="notes"/>}
                    name="notes"
                    initialValue={formPatient.notes}
                    className={"dentobook-form-item dentobook-simple-input" + (isMobileOnly ? " mb-112" : "")}
                >
                    <ReactQuill
                        value={formPatient.notes ?? undefined}
                        onChange={e => formPatient.notes = e}
                        modules={{
                            toolbar: richTextEditorToolbarOptions
                        }}/>
                </Form.Item>
                : null}

            <div style={{marginBottom: isMobile ? 200 : 80}}></div>

            <FormButtons>
                {mode === SELF_REGISTER_MODE ? null : <Button
                    className={"dentobook-btn-secondary modal-btn"}
                    htmlType="button" onClick={() => openModal()}
                    type="default">
                    {<FormattedMessage id="cancel"/>}
                </Button>}
                <Button
                    className={"dentobook-btn-primary modal-btn"}
                    onClick={handleSubmit} htmlType="submit"
                    type="primary">
                    {<FormattedMessage id="save"/>}
                </Button>
            </FormButtons>
        </Form>
    );
}

export default PatientForm;